/* eslint-disable react/no-danger */
import '../styles.scss';
import React from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { Container } from '@edx/paragon';
import { defineMessages, injectIntl } from 'gatsby-plugin-react-intl';
import { intlShape } from '@prospectus/common/types/intlShape';
import ContentfulImage from '@prospectus/common/ui/ContentfulImage';
import PreviewExpand from '@prospectus/common/ui/PreviewExpand';
import CloudflareImage from '@prospectus/common/ui/CloudflareImage';
import AtAGlance from '../../AtAGlance';
import { RtlWrapper } from '../../../../../utils/rtlLanguageHoc';
import coursesWithOverridenCollaboratorsText from '../constants';

function CourseMainDesktop({
  course,
  course: {
    uuid,
    fullDescription,
    outcome,
    syllabusRaw,
    learnerTestimonials,
    sponsors,
    collaborators,
    awards,
  },
  programs,
  intl,
  commonMessages,
  refMapProp,
}) {
  const messages = defineMessages({
    'prospectus.course.detail.main.about': {
      id: 'prospectus.course.detail.main.about',
      description: 'Heading to introduce a description of a course',
      defaultMessage: 'About this course',
    },
    'prospectus.course.detail.main.outcome': {
      id: 'prospectus.course.detail.main.outcome',
      description: 'Heading to introduce the desired outcome of a course',
      defaultMessage: 'What you\'ll learn',
    },
    'prospectus.course.detail.main.syllabus': {
      id: 'prospectus.course.detail.main.syllabus',
      description: 'Heading to introduce the syllabus of a course',
      defaultMessage: 'Syllabus',
    },
    'prospectus.course.detail.main.learner-testimonials': {
      id: 'prospectus.course.detail.main.learner-testimonials',
      description: 'Heading to introduce learner testimonials for a course',
      defaultMessage: 'Learner testimonials',
    },
    'prospectus.course.detail.main.sponsors': {
      id: 'prospectus.course.detail.main.sponsors',
      description: 'Heading for sponsors section',
      defaultMessage: 'Sponsors',
    },

    'prospectus.course.detail.main.sponsor_logo_alt': {
      id: 'prospectus.course.detail.main.sponsor_logo_alt',
      description: 'Alt text for an image of a sponsor logo.',
      defaultMessage: '{sponsorTitle} logo',
    },
    'prospectus.course.detail.main.sponsor_description': {
      id: 'prospectus.course.detail.main.sponsor_description',
      description: 'Description of sponsor\'s contribution',
      defaultMessage: 'The production of this course would not have been possible without the generous contribution of {sponsorTitle}.',
    },
    'prospectus.course.detail.main.collaborators': {
      id: 'prospectus.course.detail.main.collaborators',
      description: 'Header preceding list of course collaborators',
      defaultMessage: 'Course created with support from',
    },
    'prospectus.course.detail.main.collaborators.v2': {
      id: 'prospectus.course.detail.main.collaborators.v2',
      description: 'Header preceding list of course collaborators',
      defaultMessage: 'Course co-created with',
    },
    'prospectus.course.detail.awards.title': {
      id: 'prospectus.course.detail.awards.title',
      description: 'The header for a section that contains course award images.',
      defaultMessage: 'Awards',
    },
  });

  const isSpanish = intl.locale === 'es';

  const sponsorImageParams = {
    width: 'auto', height: 'auto', quality: 75, format: 'webp',
  };
  const collaboratorImageParams = {
    width: 200, height: 100, quality: 75, format: 'webp',
  };

  const collaboratorText = coursesWithOverridenCollaboratorsText.includes(uuid)
    ? intl.formatMessage(messages['prospectus.course.detail.main.collaborators.v2'])
    : intl.formatMessage(messages['prospectus.course.detail.main.collaborators']);

  const awardHasRequiredData = (award) => {
    const {
      englishImage,
      spanishImage,
      englishAltText,
      spanishAltText,
    } = award;

    return englishImage && englishImage.url
    && spanishImage && spanishImage.url
    && englishAltText.length > 0
    && spanishAltText.length > 0;
  };
  return (
    <Container size="sm" className="course-main">
      <div>
        <RtlWrapper propsNames={['dangerouslySetInnerHTML']}>
          <PreviewExpand
            cta={{
              labelToExpand: intl.formatMessage(commonMessages.showMore),
              labelToMinimize: intl.formatMessage(commonMessages.showLess),
              id: 'about-this-course',
              className: 'btn btn-link mt-2 pt-1 pt-sm-2 px-0 pb-0',
            }}
            heading={(
              <h1 ref={refMapProp.aboutRef} className="nav-target pt-3 mt-3 pt-sm-4 mt-sm-3">
                {intl.formatMessage(messages['prospectus.course.detail.main.about'])}
              </h1>
          )}
            sectionTitle={intl.formatMessage(messages['prospectus.course.detail.main.about'])}
            excludeIcon
            innerClassName="m-0"
          >
            <div className="mt-2 lead-sm html-data" dangerouslySetInnerHTML={{ __html: fullDescription }} />
            {sponsors && sponsors.length > 0
            && (
              <div className="pt-3">
                <h3 className="h3">
                  {intl.formatMessage(messages['prospectus.course.detail.main.sponsors'])}
                </h3>
                {sponsors.map((sponsor) => (
                  <div className="text-center" key={`sponsor-${sponsor.id}`}>
                    <div className="d-flex">
                      <div className="sponsor-logo-container">
                        <CloudflareImage
                          url={sponsor.logoImageUrl}
                          title={intl.formatMessage(messages['prospectus.course.detail.main.sponsor_logo_alt'], { sponsorTitle: sponsor.name })}
                          additionalParams={sponsorImageParams}
                          className="sponsor-img"
                        />
                      </div>
                      <div className="text-left pl-2 sponsor-text">
                        {intl.formatMessage(messages['prospectus.course.detail.main.sponsor_description'], { sponsorTitle: sponsor.name })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {collaborators && collaborators.length > 0
            && (
              <div className="mt-2">
                <h2 className="h2">
                  {collaboratorText}
                </h2>
                <div className="d-flex mt-1 flex-wrap">
                  {collaborators.map(collaborator => (
                    collaborator.image && collaborator.image.original && collaborator.image.original.url && <CloudflareImage className="mr-3" url={collaborator.image.original.url} title={collaborator.name} additionalParams={collaboratorImageParams} key={`collaborator-${collaborator.id}`} />
                  ))}
                </div>
              </div>
            )}
          </PreviewExpand>
        </RtlWrapper>
        {awards.length > 0
          && (
            <div className="py-4 mt-2">
              <h3 className="h3 text-black">
                {intl.formatMessage(messages['prospectus.course.detail.awards.title'])}
              </h3>
              <div className="d-flex align-items-center mt-3">
                {awards.map(award => {
                  if (awardHasRequiredData(award)) {
                    if (isSpanish) {
                      return (
                        <ContentfulImage
                          key={`award-${award.id}`}
                          {...award.spanishImage}
                          width={null}
                          description={award.spanishAltText}
                          className={`course-award mr-3 ${award.orientation.toLowerCase()}`}
                          additionalParams={{ q: 50 }}
                        />
                      );
                    }

                    return (
                      <ContentfulImage
                        key={`award-${award.id}`}
                        {...award.englishImage}
                        width={null}
                        description={award.englishAltText}
                        className={`course-award mr-3 ${award.orientation.toLowerCase()}`}
                        additionalParams={{ q: 50 }}
                      />
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          )}
        <AtAGlance
          {...course}
          programs={programs}
        />
        {outcome
          && (
            <div>
              <RtlWrapper propsNames={['dangerouslySetInnerHTML']}>
                <PreviewExpand
                  cta={{
                    labelToExpand: intl.formatMessage(commonMessages.showMore),
                    labelToMinimize: intl.formatMessage(commonMessages.showLess),
                    id: 'outcome',
                    className: 'btn btn-link mt-2 pt-1 pt-sm-2 px-0 pb-0',
                  }}
                  heading={(
                    <h1 ref={refMapProp.learnRef} className="h1 nav-target pt-3 mt-3 pt-sm-5 mt-sm-2">
                      {intl.formatMessage(messages['prospectus.course.detail.main.outcome'])}
                    </h1>
                )}
                  sectionTitle={intl.formatMessage(messages['prospectus.course.detail.main.outcome'])}
                  excludeIcon
                  innerClassName="m-0"
                >
                  <div className="mt-2 html-data" dangerouslySetInnerHTML={{ __html: outcome }} />
                </PreviewExpand>
              </RtlWrapper>
            </div>
          )}
        {syllabusRaw
          && (
            <div>
              <RtlWrapper propsNames={['dangerouslySetInnerHTML']}>
                <PreviewExpand
                  cta={{
                    labelToExpand: intl.formatMessage(commonMessages.showMore),
                    labelToMinimize: intl.formatMessage(commonMessages.showLess),
                    id: 'syllabus',
                    className: 'btn btn-link mt-2 pt-1 pt-sm-2 px-0 pb-0',
                  }}
                  heading={(
                    <h1 ref={refMapProp.syllabusRef} className="h1 nav-target pt-3 mt-3 pt-sm-5 mt-sm-2">
                      {intl.formatMessage(messages['prospectus.course.detail.main.syllabus'])}
                    </h1>
                  )}
                  sectionTitle={intl.formatMessage(messages['prospectus.course.detail.main.syllabus'])}
                  excludeIcon
                  innerClassName="m-0"
                >
                  <div className="mt-2 html-data" dangerouslySetInnerHTML={{ __html: syllabusRaw }} />
                </PreviewExpand>
              </RtlWrapper>
            </div>
          )}
        {learnerTestimonials
          && (
            <div>
              <RtlWrapper propsNames={['learnerTestimonials']}>
                <PreviewExpand
                  cta={{
                    labelToExpand: intl.formatMessage(commonMessages.showMore),
                    labelToMinimize: intl.formatMessage(commonMessages.showLess),
                    id: 'testimonials',
                    className: 'btn btn-link mt-2 pt-1 pt-sm-2 px-0 pb-0',
                  }}
                  heading={(
                    <h1 className="h1 pt-3 mt-3 pt-sm-5 mt-sm-2">
                      {intl.formatMessage(messages['prospectus.course.detail.main.learner-testimonials'])}
                    </h1>
                  )}
                  sectionTitle={intl.formatMessage(messages['prospectus.course.detail.main.learner-testimonials'])}
                  excludeIcon
                  innerClassName="m-0"
                >
                  <div className="mt-2 html-data" dangerouslySetInnerHTML={{ __html: learnerTestimonials }} />
                </PreviewExpand>
              </RtlWrapper>
            </div>
          )}
      </div>
    </Container>
  );
}

CourseMainDesktop.propTypes = {
  course: PropTypes.shape({
    uuid: PropTypes.string,
    fullDescription: PropTypes.string,
    outcome: PropTypes.string,
    syllabusRaw: PropTypes.string,
    learnerTestimonials: PropTypes.string,
    sponsors: PropTypes.arrayOf(PropTypes.shape({
      logoImageUrl: PropTypes.string.isRequired,
      name: PropTypes.string,
    })),
    collaborators: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      image: PropTypes.shape({}),
    })),
    awards: PropTypes.arrayOf(PropTypes.shape({
      orientation: PropTypes.string,
      englishAltText: PropTypes.string,
      spanishAltText: PropTypes.string,
      englishImage: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      spanishImage: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    })),
  }).isRequired,
  refMapProp: PropTypes.shape({
    aboutRef: PropTypes.shape(),
    learnRef: PropTypes.shape(),
    syllabusRef: PropTypes.shape(),
  }).isRequired,
  programs: PropTypes.arrayOf(PropTypes.shape({})),
  intl: intlShape.isRequired,
  commonMessages: PropTypes.shape({
    showMore: PropTypes.shape({}),
    showLess: PropTypes.shape({}),
  }).isRequired,
};

CourseMainDesktop.defaultProps = {
  programs: [],
};

export default track({
  component: 'course-main',
})(injectIntl(CourseMainDesktop));
